import { Check, ArrowRight, Map, BadgeCheck, MessageSquare, ClipboardList } from "lucide-react";
import { ShieldPanel } from "./ShieldPanel";
import { HOST, TRANSFORMS, TAKEAWAYS, BG_IMAGES } from "../lib/content";
import { Reveal, scrollToId } from "../lib/anim";

const T_ICONS = { Map, BadgeCheck, MessageSquare, ClipboardList };

export const HostSection = () => (
  <section id="host" className="px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="host-section">
    <Reveal>
      <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-14">[005] — YOUR HOST</p>
    </Reveal>
    <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
      <Reveal>
        <div className="max-w-md">
          <ShieldPanel>
            <img
              src={HOST.portrait}
              alt="Najiyyah Qaasim"
              data-parallax="0.1"
              className="absolute inset-0 w-full h-full object-cover opacity-90 scale-110"
              data-testid="host-portrait-img"
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(9,30,66,0.35) 0%, transparent 30%, transparent 60%, rgba(9,30,66,0.75) 100%)" }} />
          </ShieldPanel>
        </div>
      </Reveal>
      <div>
        <Reveal>
          <h2 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(28px,3.4vw,46px)] text-[#D9E4F5] mb-6">
            GUIDANCE FROM SOMEONE WHO WORKS <span className="text-[#FFFFFF]">INSIDE THE PROCESS.</span>
          </h2>
          <div className="w-20 h-px bg-[#FFFFFF] mb-8" />
          <div className="space-y-5 text-[15px] text-white/55 leading-[1.8] mb-10">
            <p>
              I'm Najiyyah Qaasim, a licensed insurance professional with {HOST.company}. My work is helping homeowners who carry a mortgage plus other debt — the families making every payment on time and still watching their payoff date sit decades away.
            </p>
            <p>
              Here's what I've learned inside this process: most people don't struggle with debt because of discipline. They struggle because the standard payoff math is stacked against them — amortization schedules front-load interest, and minimum payments are designed to keep balances alive. Nobody explains that, so people blame themselves.
            </p>
            <p>
              Your free strategy call exists to close that gap. In one focused session, I review your debt profile, check whether you qualify — Debt-Free Life is designed for qualified clients, not everyone — and show you how your payoff may be structured, with your own numbers in front of you. If it fits, I guide you through every step. If it doesn't, you'll know quickly and honestly. No guarantees, no pressure. Just clarity.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-10" data-testid="host-credential-grid">
            {[
              ["COMPANY", HOST.company],
              ["LICENSE #", HOST.license],
              ["LICENSED IN", HOST.licenseStates],
              ["SPECIALTY", "Debt Elimination Strategy"],
            ].map(([label, value]) => (
              <div key={label} className="bg-[#0E2A54] p-5">
                <p className="font-mono text-[9px] tracking-[0.3em] text-white/35 mb-2">{label}</p>
                <p className="text-[13px] font-semibold text-[#D9E4F5]">{value}</p>
              </div>
            ))}
          </div>
          <button
            data-testid="host-cta-btn"
            data-cta="true"
            onClick={() => scrollToId("#register")}
            className="inline-flex items-center gap-2 bg-[#FFFFFF] text-[#0E2A54] font-bold text-sm tracking-wide px-8 py-4 hover:bg-[#DCE8FF] transition-colors duration-200"
          >
            <Check className="w-4 h-4" /> BOOK MY FREE CALL
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Transformation = () => (
  <section className="relative overflow-hidden bg-white/[0.015] px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="transformation-section">
    <div className="absolute inset-0 pointer-events-none" data-testid="transformation-bg-image">
      <img src={BG_IMAGES.familyHome} alt="" data-parallax="0.08" className="w-full h-full object-cover opacity-[0.22] scale-110" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #0E2A54 0%, rgba(9,30,66,0.7) 28%, rgba(9,30,66,0.7) 72%, #0E2A54 100%)",
        }}
      />
    </div>
    <div className="relative grid md:grid-cols-2 gap-14 lg:gap-24">
      <Reveal>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-6">[006] — THE SHIFT</p>
        <h2 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(28px,3.4vw,46px)] text-[#D9E4F5] mb-8">
          IF YOU'RE LIKE MOST HOMEOWNERS I WORK WITH…
        </h2>
        <div className="space-y-5 text-[15px] text-white/55 leading-[1.8]">
          <p>
            …you've done everything you were told. You pay on time. You round up when you can. Maybe you've tried the snowball, the avalanche, the extra principal payment. And yet the mortgage statement still shows a payoff date twenty-plus years out — with the credit cards and student loans circling like they'll never land. The stress is constant, and the future feels stuck on hold.
          </p>
          <p>
            Here's what I want you to hear: that's not a discipline problem. It's a strategy problem. Financially responsible people get stuck every day because nobody shows them which lever actually moves the timeline — or that the money they're already spending may be able to do double duty: paying off debt sooner while building toward retirement. That's the conversation this call exists to have.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-8">HERE'S WHAT CHANGES AFTER YOUR CALL:</p>
        <ul className="space-y-6 mb-12">
          {TRANSFORMS.map((t, i) => (
            <li key={i} className="flex items-start gap-4" data-testid={`transform-bullet-${i + 1}`}>
              <ArrowRight className="w-5 h-5 text-[#FFFFFF] shrink-0 mt-0.5" />
              <p className="text-[16px] text-[#D9E4F5]/85 leading-relaxed">{t}</p>
            </li>
          ))}
        </ul>
        <button
          data-testid="transformation-cta-btn"
          data-cta="true"
          onClick={() => scrollToId("#register")}
          className="inline-flex items-center gap-2 bg-[#FFFFFF] text-[#0E2A54] font-bold text-sm tracking-wide px-8 py-4 hover:bg-[#DCE8FF] transition-colors duration-200"
        >
          <Check className="w-4 h-4" /> BOOK MY FREE CALL
        </button>
      </Reveal>
    </div>
  </section>
);

export const Takeaways = () => (
  <section className="px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="takeaways-section">
    <Reveal>
      <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-6">[007] — TAKEAWAYS</p>
      <h2 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(30px,4vw,54px)] text-[#D9E4F5] mb-14">
        WHAT YOU'LL WALK AWAY WITH
      </h2>
    </Reveal>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
      {TAKEAWAYS.map((t, i) => {
        const Icon = T_ICONS[t.icon];
        return (
          <Reveal key={t.title} delay={i * 0.06}>
            <div className="h-full p-7 border border-white/10 hover:border-[#FFFFFF]/60 transition-colors duration-300" data-testid={`takeaway-card-${i + 1}`}>
              <Icon className="w-6 h-6 text-[#FFFFFF] mb-6" />
              <h3 className="text-[15px] font-bold text-[#D9E4F5] mb-3">{t.title}</h3>
              <p className="text-[13px] text-white/45 leading-relaxed">{t.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);
